import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LoadingButton from "@mui/lab/LoadingButton"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import {
  MULTIPART_HEADERS,
  useAuthorizedAxiosClient,
} from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import SegmentTracker from "../../../common/tracking"
import { getEmailEditorHTML } from "../email-campaigns/edit-email-mixin"
import ConfirmDeleteEmailCampaignDialog from "../email-campaigns/update/ConfirmDeleteEmailCampaignDialog"
import { selectCRMSettings } from "../reducer"
import {
  AUTOCOMPLETE_OPTIONS,
  CAMPAIGN_OPERATIONS,
  useDialogContainer,
} from "../utils"
import AIGenButton, { useAIGenButtonState } from "./AIGenButton"
import AddAttachmentsSection, {
  useAddAttachmentSectionState,
} from "./AddAttachmentsSection"
import BrokerInfoTooltip from "./BrokerInfoTooltip"
import DialogHeader from "./DialogHeader"
import EmailWYSWIGEditor, {
  useEmailWYSWIGEditorState,
} from "./EmailWYSWIGEditor"
import GenerateEmailOrSMSCampaignPromptPopover from "./GenerateEmailOrSMSCampaignPromptPopover"
import RenderedEmailBody from "./RenderedEmailBody"
import {
  EMAIL_SIGNATURE_API_URL,
  buildSignaturePreviewHtml,
  parseIncludeSignature,
  savedSignatureHasContent,
} from "./emailSignatureUtils"

export default function UpdateCreateEmailCampaignDialog({
  open,
  onClose,
  campaign,
  renderedBody,
  onError,
}) {
  const [name, setName] = useState("")
  const { aiGenerateButtonRef, setAIGenerateButtonRef } = useAIGenButtonState()
  const [subject, setSubject] = useState("")
  const {
    editorReady,
    setEditorReady,
    insertLinkDialogOpen,
    setInsertLinkDialogOpen,
    editorRef,
    setEditorBody,
  } = useEmailWYSWIGEditorState(open)
  const [preHeader, setPreHeader] = useState("")
  const [cc, setCC] = useState([])
  const [includeSignature, setIncludeSignature] = useState(false)
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("")
  const {
    fileAttachments,
    setFileAttachments,
    fileAttachmentError,
    setFileAttachmentError,
    setDeletedAttachments,
    addAttachmentsToFormData,
    convertBackendAttachmentsToFileAttachments,
  } = useAddAttachmentSectionState()

  const [isCreating, setIsCreating] = useState(false)
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)
  const crmSettings = useSelector(selectCRMSettings)
  const axios = useAuthorizedAxiosClient()
  const Container = useDialogContainer()
  const createButtonDisabled = !name || !subject

  function createCampaign(callback) {
    return callBackend(
      getCreatePromise,
      CAMPAIGN_OPERATIONS.created,
      callback
    ).then(() =>
      SegmentTracker.track("Email template created", {
        Name: name,
        Subject: subject,
      })
    )
  }

  function updateCampaign(callback, campaign) {
    return callBackend(
      getUpdatePromise,
      CAMPAIGN_OPERATIONS.updated,
      callback,
      campaign
    )
  }

  function handleGenerateCampaignPromptPopoverClosed(generatedCampaign) {
    setAIGenerateButtonRef(null)
    if (generatedCampaign) {
      setName(generatedCampaign.subject)
      setSubject(generatedCampaign.subject)
      const html = generatedCampaign.content.replace(/\r?\n/g, "<br/>")
      setEditorBody(html, includeSignature, signaturePreviewHtml)
    }
  }

  function getEmailCampaignAPIData() {
    const formData = new FormData()
    formData.append("name", name)
    formData.append("subject", subject)
    formData.append("pre_header", preHeader)
    for (const c of cc) {
      formData.append("cc", c)
    }
    formData.append("html_body", getEmailEditorHTML(editorRef))
    formData.append("include_signature", includeSignature ? "true" : "false")
    addAttachmentsToFormData(formData)
    return formData
  }

  function getCreatePromise() {
    return axios.post(
      "/workflows/api/email/",
      getEmailCampaignAPIData(),
      MULTIPART_HEADERS
    )
  }

  function getUpdatePromise(campaign) {
    return axios.put(
      `/workflows/api/email/${campaign.id}/`,
      getEmailCampaignAPIData(),
      MULTIPART_HEADERS
    )
  }

  function callBackend(getPromiseFunc, campaignOperation, callback, campaign) {
    setIsCreating(true)
    return getPromiseFunc(campaign)
      .then(({ data }) => callback(data, campaignOperation))
      .catch((error) => {
        const message = parseApiErrorMessage(
          error.response?.data,
          "Could not save email template"
        )
        onError?.(message)
        throw error
      })
      .finally(() => setIsCreating(false))
  }

  function handleSoftClose() {
    onClose()
  }

  function handleSaveCreateButtonClicked() {
    const actionFunc = campaign ? updateCampaign : createCampaign
    actionFunc(onClose, campaign)
  }

  function handleConfirmDeleteDialogClosed(deleted) {
    if (deleted) {
      onClose(campaign, CAMPAIGN_OPERATIONS.deleted)
    }
    setConfirmDeleteDialogOpen(false)
  }

  function loadSignaturePreview() {
    return axios.get(EMAIL_SIGNATURE_API_URL).then(({ data }) => {
      const signatureHtml = buildSignaturePreviewHtml(data)
      setSignaturePreviewHtml(signatureHtml)
      return { data, signatureHtml }
    })
  }

  function updateFields(
    name,
    subject,
    preHeader,
    cc,
    includeSignature,
    editorRefContents,
    attachments,
    signatureHtml = signaturePreviewHtml
  ) {
    setName(name)
    setSubject(subject)
    setPreHeader(preHeader)
    setCC(cc)
    setIncludeSignature(includeSignature)
    setEditorBody(editorRefContents, includeSignature, signatureHtml)
    convertBackendAttachmentsToFileAttachments(attachments)
  }

  function loadCampaignFields(campaignData, signatureHtml) {
    const includeSig = parseIncludeSignature(campaignData.include_signature)
    updateFields(
      campaignData.name,
      campaignData.subject,
      campaignData.pre_header || "",
      campaignData.cc || [],
      includeSig,
      campaignData.html_body,
      campaignData.attachments || [],
      signatureHtml
    )
  }

  useEffect(() => {
    if (!open) {
      setSignaturePreviewHtml("")
      setIncludeSignature(false)
      setName("")
      setSubject("")
      setPreHeader("")
      setCC([])
    }
  }, [open])

  useEffect(
    () => {
      if (!open || !editorReady) {
        return
      }

      if (campaign?.id) {
        loadSignaturePreview().then(({ signatureHtml }) =>
          axios
            .get(`/workflows/api/email/${campaign.id}/`)
            .then(({ data }) => loadCampaignFields(data, signatureHtml))
            .catch(() => loadCampaignFields(campaign, signatureHtml))
        )
      } else if (campaign) {
        loadSignaturePreview().then(({ signatureHtml }) =>
          loadCampaignFields(campaign, signatureHtml)
        )
      } else {
        loadSignaturePreview()
          .then(({ data, signatureHtml }) => {
            updateFields(
              "",
              "",
              "",
              [],
              savedSignatureHasContent(data),
              "",
              [],
              signatureHtml
            )
          })
          .catch(() => {
            updateFields("", "", "", [], false, "", [], "")
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, campaign?.id, editorReady]
  )

  return (
    <Container open={open} onClose={handleSoftClose} anchor="right" keepMounted>
      <DialogHeader
        title={`${campaign ? "Edit" : "Create"} Email Template`}
        onClose={handleSoftClose}
      />
      <ConditionalPurpleLinearProgress visible={crmSettings.loading} />
      <DialogContent>
        <Column gap={1}>
          <BrokerInfoTooltip
            crmSettings={crmSettings.settings}
            crmSettingsLoaded={!crmSettings.loading}
          />
          <Column gap={2}>
            <Row sx={{ gap: 1 }}>
              <LengthLimitedTextField
                label="Template name"
                value={name}
                setValue={setName}
                maxLength={188}
                required
              />
              <AIGenButton
                campaign={campaign}
                setAIGenerateButtonRef={setAIGenerateButtonRef}
              />
              <GenerateEmailOrSMSCampaignPromptPopover
                anchorElem={aiGenerateButtonRef}
                onClose={handleGenerateCampaignPromptPopoverClosed}
                apiType="email"
              />
            </Row>
            <LengthLimitedTextField
              label="Subject"
              value={subject}
              setValue={setSubject}
              maxLength={988}
              required
            />
            <LengthLimitedTextField
              label="Preheader"
              error={false}
              helperText="Introduction text that shows below subject in some email clients"
              value={preHeader}
              setValue={setPreHeader}
              maxLength={130}
            />
            <Autocomplete
              renderInput={(params) => (
                <TextField {...params} label="CC" size="small" />
              )}
              value={cc}
              onChange={(_, newValue) => setCC(newValue)}
              options={[]}
              multiple
              freeSolo
              limitTags={2}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSignature}
                  onChange={(event) =>
                    setIncludeSignature(event.target.checked)
                  }
                />
              }
              label="Include email signature when sending this template"
            />
            {open && (
              <EmailWYSWIGEditor
                editorRef={editorRef}
                editorReady={editorReady}
                setEditorReady={setEditorReady}
                insertLinkDialogOpen={insertLinkDialogOpen}
                setInsertLinkDialogOpen={setInsertLinkDialogOpen}
                includeSignature={includeSignature}
                signaturePreviewHtml={signaturePreviewHtml}
                onSetup={(editor) =>
                  editor.ui.registry.addMenuButton("insert-agent-data", {
                    text: "Insert placeholder",
                    fetch: (callback) => {
                      callback(
                        AUTOCOMPLETE_OPTIONS.map((option) => ({
                          type: "menuitem",
                          text: option.label,
                          onAction: () =>
                            editor.insertContent(`{{ ${option.fieldName} }}`),
                        }))
                      )
                    },
                  })
                }
              />
            )}
            {includeSignature && !signaturePreviewHtml ? (
              <Typography variant="caption" color="text.secondary">
                No email signature saved yet. Add one in Settings → Email to
                include it with this template.
              </Typography>
            ) : null}
          </Column>
          {renderedBody ? (
            <Accordion
              defaultExpanded={false}
              sx={{ "&.MuiPaper-root": { width: "100%" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5">Actual email sent</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RenderedEmailBody renderedBody={renderedBody} />
              </AccordionDetails>
            </Accordion>
          ) : null}
          <AddAttachmentsSection
            fileAttachments={fileAttachments}
            setFileAttachments={setFileAttachments}
            fileAttachmentError={fileAttachmentError}
            setFileAttachmentError={setFileAttachmentError}
            setDeletedAttachments={setDeletedAttachments}
          />
        </Column>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: campaign ? "space-between" : "flex-end" }}
      >
        {campaign ? (
          <Button
            color="error"
            variant="contained"
            onClick={() => setConfirmDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        ) : null}
        <Row gap={1}>
          <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            disabled={createButtonDisabled}
            loading={isCreating}
            onClick={handleSaveCreateButtonClicked}
          >
            {campaign ? "Save" : "Create"}
          </LoadingButton>
        </Row>
      </DialogActions>
      {campaign ? (
        <ConfirmDeleteEmailCampaignDialog
          open={confirmDeleteDialogOpen}
          onClose={handleConfirmDeleteDialogClosed}
          emailCampaign={campaign}
        />
      ) : null}
    </Container>
  )
}

export function useUpdateCreateEmailCampaignDialogState() {
  const [createCampaignDialogOpen, setCreateCampaignDialogOpen] =
    useState(false)

  function handleCreateCampaignDialogClosed(
    setAvailableCampaigns,
    onCampaignCreated = () => {}
  ) {
    return (createdCampaign) => {
      if (createdCampaign) {
        setAvailableCampaigns((availableCampaigns) =>
          availableCampaigns.concat(createdCampaign)
        )
        onCampaignCreated(createdCampaign)
      }
      setCreateCampaignDialogOpen(false)
    }
  }

  return {
    createCampaignDialogOpen,
    setCreateCampaignDialogOpen,
    handleCreateCampaignDialogClosed,
  }
}
